import { useCore } from '@/composables/core'
import { ENDPOINTS } from '@/lib/consts'
import type { Store } from 'vuex'
import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { useLogger, useReadyTrigger } from '@booksy-com/vue-tools'
import type { RootState } from '@/types/store'

/** Options passed through to the Zowie / Chatbotize `open` handler. */
export type ChatOpenOptions = {
  referral?: string | null
}

type ChatProviderAction = 'open' | 'close' | 'toggle' | 'logout'

type ChatProvider = {
  open?: (options?: ChatOpenOptions) => void
  close?: () => void
  toggle?: () => void
  logout?: () => void
}

interface ZowieJwtResponseBody {
  token: string
  instance_id?: string
  [key: string]: unknown
}

export interface UseChatResult {
  open: (options?: ChatOpenOptions) => Promise<void>
  close: () => Promise<void>
  toggle: () => Promise<void>
}

let chatProvider: ChatProvider | null = null

const logger = useLogger('chat')

type ChatReadyTrigger = ReturnType<typeof useReadyTrigger> & {
  isResolved?: boolean
}

let readyTrigger: ChatReadyTrigger | null = null
let shouldOpenWhenReady = false

const _logUninitializedError = (e?: unknown): void => {
  logger.error('chat provider not initialized')
  if (e) {
    logger.error(e)
  }
}

const _handleChatAction = (
  method: ChatProviderAction,
  options?: ChatOpenOptions
): Promise<void> => {
  if (!readyTrigger) {
    _logUninitializedError()
    return Promise.resolve()
  }

  return readyTrigger.promise
    .then(() => {
      const fn = chatProvider?.[method]
      if (fn) {
        ;(fn as (opts?: ChatOpenOptions) => void)(options)
      } else {
        _logUninitializedError()
      }
    })
    .catch((e: unknown) => {
      _logUninitializedError(e)
    })
}

const open = async (options?: ChatOpenOptions): Promise<void> =>
  _handleChatAction('open', options)
const close = async (): Promise<void> => _handleChatAction('close')
const toggle = async (): Promise<void> => _handleChatAction('toggle')

export const createChat = async (): Promise<void> => {
  readyTrigger = useReadyTrigger()

  const { apiCall, store } = useCore()
  const business: ComputedRef<RootState['business']> = computed(
    () => (store as Store<RootState>).state.business
  )

  const setupChatProvider = async (): Promise<void> => {
    if (!business.value.business?.id) {
      return
    }

    try {
      const res = (await apiCall({
        method: 'POST',
        url: ENDPOINTS.ZOWIE_JWT,
        data: {
          business_id: business.value.business.id,
        },
        params: {
          new_zowie: 1,
        },
      })) as { data: ZowieJwtResponseBody }

      await import('@/composables/chat/providers/zowie/index.ts').then((Zowie) => {
        Zowie.createZowie({ data: res.data })
        chatProvider = Zowie.useZowie()
        readyTrigger!.resolveTrigger()

        // If support was requested before chat was ready, open it now
        if (shouldOpenWhenReady) {
          shouldOpenWhenReady = false
          void open()
        }
      })
    } catch (e) {
      logger.error(e)
      readyTrigger!.rejectTrigger()
    }
  }

  await setupChatProvider()
}

export const destroyChat = async (): Promise<void> => {
  return _handleChatAction('logout').then(() => {
    chatProvider = null
    readyTrigger = null
    shouldOpenWhenReady = false
  })
}

export const openSupportWhenPossible = (): void => {
  if (readyTrigger?.isResolved === true) {
    // Chat is already ready, open immediately
    shouldOpenWhenReady = false
    void open()
  } else {
    // Remember to open when chat becomes ready
    shouldOpenWhenReady = true
  }
}

export const useChat = (): UseChatResult => ({
  open,
  close,
  toggle,
})
